import { computed, effect, inject, Injectable, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, finalize, throttleTime } from 'rxjs';

import type { Condition } from 'app/api/models/condition';
import { UserStore } from 'app/core/services/user.store';
import { UserPreferenceStore } from 'app/core/services/user-preference/user-preference.store';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import type { Entry, FilterItem, Module } from 'app/features/admin/modules/modules.model';
import { InterfaceActionsService } from 'app/features/admin/modules/features/interface-actions/interface-actions.service';
import type { InterfaceAction } from 'app/features/admin/modules/features/interface-actions/interface-actions.model';
import type { GettingStartedConfig, GettingStartedStep } from 'app/features/admin/widgets/widgets.model';
import { GlobalWidgetPlacementEnum } from 'app/features/admin/widgets/widgets.model';
import type { ConditionDateCastFields } from 'app/shared/types/condition-cast.types';
import { byCodeTitleId, isRoleAllowed, placementsOf } from 'app/shared/services/global-widget.util';
import {
  ExternalEntriesEvaluatorService,
  collectExternalEntriesQueries,
} from 'app/shared/services/external-entries-evaluator.service';
import { type ExternalEntriesCount, evaluateFiltersAgainstEntry } from 'app/shared/services/filter-evaluator.util';
import { ModalService } from 'app/shared/services/modal.service';
import { debugLog } from 'app/shared/utils/debug-logger';

import type { GettingStartedStepView } from './getting-started-panel.component';
import {
  GETTING_STARTED_TEMPLATE,
  GETTING_STARTED_UI_KEY,
  type GettingStartedUiBucket,
  type GettingStartedUserState,
} from './getting-started.types';

/**
 * `handleCustomAction` is fire-and-forget (it staggers its steps through `setTimeout` and returns
 * void), so there is no completion signal to wait on. One delayed re-evaluation gives the write
 * time to land; the step also ticks on the next dashboard visit, so a slower backend is not fatal.
 */
const REFRESH_AFTER_ACTION_MS = 1200;

/**
 * Floor between two navigation-triggered evaluation passes. Long enough that moving around the app
 * does not turn into a stream of count requests, short enough that a step ticks over within a
 * couple of screens of the user having done the work.
 */
const NAVIGATION_REFRESH_THROTTLE_MS = 15_000;

/**
 * Button label for a step that has no target and completes manually — pressing it does nothing but
 * tick the step, so 'Start' (the panel's own fallback) would be misleading. An admin-authored
 * `buttonLabel` always wins.
 */
const MANUAL_DONE_LABEL = 'Markeer als gereed';

/** Conditions as the form-builder actually authors them (the generated POCO is a subset). */
type AuthoredCondition = Condition &
  ConditionDateCastFields & {
    fieldKeyPath?: string;
    subFieldKeyPath?: string;
    /** 'externalEntries' asks about other entries and carries moduleCode/filter/minCount instead. */
    moduleCode?: string;
    filter?: string;
    minCount?: number;
  };

/**
 * Runtime brain of the "Aan de slag" global widget (#20703).
 *
 * Resolves which `type:'widget'` module applies to the current user, evaluates every step's
 * completion against real data, exposes the panel-ready view models, and executes the Start button.
 *
 * Deliberate split in how completion is derived:
 * - `entriesExist` is asynchronous (one deduplicated count request per `(moduleCode, filter)` pair)
 *   and therefore cached in `#entryCounts`, refreshed per evaluation pass.
 * - `condition` and `manual` are pure functions of signals that are already in memory
 *   (`UserStore.currentUserEntry()` / the user-preference bucket), so they are evaluated live in
 *   `stepViewsFor`. Ticking a manual step or saving the user entry therefore updates the panel
 *   without waiting for a pass.
 *
 * Nothing in here throws into the UI: an unresolvable module, a malformed condition or a failing
 * count leaves the step open and logs through `debugLog('GettingStarted', …)`.
 */
@Injectable({ providedIn: 'root' })
export class GettingStartedService {
  readonly #modulesStore = inject(ModulesStore);
  readonly #userStore = inject(UserStore);
  readonly #userPreferenceStore = inject(UserPreferenceStore);
  readonly #modalService = inject(ModalService);
  readonly #interfaceActionsService = inject(InterfaceActionsService);
  readonly #externalEntries = inject(ExternalEntriesEvaluatorService);
  readonly #router = inject(Router);
  readonly #activeStartKeys = new Set<string>();

  /** Question set of the last pass; keeps the config-driven effect from re-asking the unchanged. */
  #lastQuerySignature: string | null = null;

  /** `ui.gettingStarted`, normalised per widget on read. */
  readonly #bucket = computed<GettingStartedUiBucket>(
    () => (this.#userPreferenceStore.ui()?.[GETTING_STARTED_UI_KEY] as GettingStartedUiBucket) ?? {}
  );

  /**
   * Every enabled getting-started widget the current user is allowed to see, in a stable order.
   *
   * The store's array order follows whatever the API returned, so it is explicitly re-sorted on
   * code → title → id: with two matching widgets "first wins" has to mean the same widget on every
   * reload.
   */
  readonly #candidateWidgets = computed<Module[]>(() => {
    const currentUserRoles = this.#userStore.currentUserRoles();
    return this.#modulesStore
      .enabledGlobalWidgets()
      .filter(module => module.widgetConfig?.template === GETTING_STARTED_TEMPLATE)
      .filter(module => !!module.widgetConfig?.gettingStarted)
      .filter(module => isRoleAllowed(module.widgetConfig?.allowedUserRoles, currentUserRoles))
      .sort(byCodeTitleId);
  });

  /** The widget rendered as the dashboard banner, if any. */
  readonly dashboardWidget = computed<Module | undefined>(() =>
    this.#candidateWidgets().find(module => placementsOf(module).includes(GlobalWidgetPlacementEnum.Dashboard))
  );

  /** The widget rendered as the app-shell floating card, if any. */
  readonly floatingWidget = computed<Module | undefined>(() =>
    this.#candidateWidgets().find(module => placementsOf(module).includes(GlobalWidgetPlacementEnum.Floating))
  );

  constructor() {
    // A pass as soon as the two inputs it needs are in place: the user (roles + user entry) and the
    // modules (widget documents + moduleCode → id). `UserStore.ensureLoaded()` is fired by the
    // app's guards/resolvers; this only observes the result, so instantiating the service can never
    // trigger an extra /user/me.
    //
    // Deliberately NOT a one-shot: the questions to ask are derived from the widget documents, so a
    // later modules reload (or a widget that only becomes applicable once roles resolve) has to be
    // able to trigger a pass of its own. `#lastQuerySignature` keeps that from turning into a loop
    // — an unchanged set of questions is never re-asked.
    effect(() => {
      const ready = this.#userStore.loaded() && this.#modulesStore.modulesLoaded();
      // Read inside the effect so a widget document change re-triggers it.
      const widgets = this.#candidateWidgets();
      if (!ready || !widgets.length) {
        return;
      }
      untracked(() => this.refresh());
    });

    // Re-evaluate on every navigation, including a return to the dashboard. A session that lives
    // elsewhere in the app and never re-expands the panel would otherwise resolve its counts once
    // at boot and never again, leaving every entriesExist step reading open forever. Throttling
    // makes a redirect burst cost one pass; trailing keeps the final destination fresh.
    this.#router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        throttleTime(NAVIGATION_REFRESH_THROTTLE_MS, undefined, { leading: true, trailing: true }),
        takeUntilDestroyed()
      )
      .subscribe(() => this.refresh({ force: true }));
  }

  /** Admin-authored config of a widget document. */
  configFor(module: Module | undefined): GettingStartedConfig | undefined {
    return module?.widgetConfig?.gettingStarted;
  }

  /**
   * Panel-ready steps: ordered as authored, 1-based index, live state.
   *
   * Pure signal reads only, so mounts can wrap this in a `computed()`.
   */
  stepViewsFor(module: Module | undefined): GettingStartedStepView[] {
    return module ? (this.#stepViewsByWidget()[module.id] ?? []) : [];
  }

  /**
   * Step views of every applicable widget, evaluated once per relevant signal change.
   *
   * Without this each mount re-entered the evaluation per accessor — `visible()`, `config`, `steps`
   * and `collapsed` each triggered a full pass, so one signal change cost four-plus evaluations and
   * four fresh arrays. A computed collapses that to one, and the stable array reference lets OnPush
   * skip work it would otherwise redo.
   *
   * The reference is stable only between dependency changes, and the map is rebuilt wholesale: with
   * two widgets configured, collapsing the banner writes `ui` and hands the floating panel a fresh
   * array too. Harmless over-invalidation — do not build anything on per-widget identity holding
   * across an unrelated widget's state change.
   */
  readonly #stepViewsByWidget = computed<Record<string, GettingStartedStepView[]>>(() => {
    const byWidget: Record<string, GettingStartedStepView[]> = {};
    for (const module of this.#candidateWidgets()) {
      byWidget[module.id] = this.#buildStepViews(module);
    }
    return byWidget;
  });

  #buildStepViews(module: Module): GettingStartedStepView[] {
    const config = this.configFor(module);
    if (!config?.steps?.length) {
      return [];
    }

    const state = this.#stateFor(module.id);
    const counts = this.#externalEntries.counts();
    const userEntry = this.#userStore.currentUserEntry();

    return config.steps.map((step, index) => {
      const completed = this.#isStepCompleted(step, state, counts, userEntry);
      const targetType = step.target?.type ?? 'none';
      // A `manual` step with no target would otherwise be un-tickable: the panel only renders an
      // affordance when showStartButton is true, and pressing it on a target-less step is exactly
      // what records the manual tick (see #markManualDoneIfManual). That combination is also the
      // default of a freshly added step in the admin form, so it must work.
      const isManualOnly = targetType === 'none' && step.completion?.type === 'manual';
      // Data wins over a skip: once a step is genuinely done it reads as done, even if the user
      // skipped it earlier.
      const stepState: GettingStartedStepView['state'] = completed
        ? 'completed'
        : state.skippedStepIds.includes(step.id)
          ? 'skipped'
          : 'open';

      return {
        id: step.id,
        index: index + 1,
        title: step.title ?? '',
        description: step.description,
        // The panel falls back to 'Start', which reads wrong on a button that only ticks the step.
        buttonLabel: step.buttonLabel?.trim() || (isManualOnly ? MANUAL_DONE_LABEL : ''),
        state: stepState,
        showStartButton: targetType !== 'none' || isManualOnly,
        skippable: !!step.skippable,
      };
    });
  }

  // completedCount()/totalCount() used to live here but were never called: the panel derives both
  // from the step views it already has (getting-started-panel.component.ts), so a second source of
  // the same numbers was only a way for them to disagree.

  /**
   * Should the panel render at all.
   *
   * A skipped step counts as resolved for `hideWhenComplete`: skipping is the user saying they are
   * not going to do it, so it must not keep the panel alive forever.
   */
  visible(module: Module | undefined): boolean {
    const config = this.configFor(module);
    if (!module || !config?.steps?.length) {
      return false;
    }

    // Only these two make visibility depend on state that arrives late; a widget that always shows
    // must keep painting on the first frame rather than wait for data it does not consult.
    if ((config.hideWhenComplete || config.dismissible) && !this.#stateSettled(module, config)) {
      return false;
    }

    const state = this.#stateFor(module.id);
    if (config.dismissible && state.dismissed) {
      return false;
    }

    if (config.hideWhenComplete) {
      const steps = this.stepViewsFor(module);
      if (steps.every(step => step.state !== 'open')) {
        return false;
      }
    }

    return true;
  }

  /**
   * Are the inputs `visible()` consults actually resolved, or still at their pre-load defaults?
   *
   * Both of them start out lying in the same direction. Before the user preferences land nothing is
   * dismissed and no step is manually done; before the counts land every `entriesExist` step reads
   * open. So a fully completed widget evaluated on those defaults is "incomplete and not dismissed"
   * — it painted on the first frame of every reload and vanished again a request later.
   *
   * Settled means asked-and-came-back, not answered: a count that keeps failing leaves its steps
   * open and the panel visible, which is the honest reading when the data cannot be had.
   */
  #stateSettled(module: Module, config: GettingStartedConfig): boolean {
    if (!this.#userPreferenceStore.loaded()) {
      return false;
    }
    if (!config.hideWhenComplete) {
      return true;
    }
    return collectExternalEntriesQueries(this.#stepFiltersFor(module)).every(query =>
      this.#externalEntries.hasSettled(query.key, query.top)
    );
  }

  /** Collapsed = header only. Defaults to collapsed (the 92px Figma banner). */
  collapsed(module: Module | undefined): boolean {
    return module ? this.#stateFor(module.id).collapsed : true;
  }

  /**
   * Execute a step's Start button. Every branch is a no-op-with-log when its configuration does not
   * resolve — a misconfigured step must not break the dashboard.
   */
  start(module: Module | undefined, stepId: string): void {
    const step = this.configFor(module)?.steps?.find(candidate => candidate.id === stepId);
    if (!module || !step) {
      debugLog('GettingStarted', 'start: step not found', { widgetId: module?.id, stepId });
      return;
    }

    const startKey = `${module.id}:${step.id}`;
    if (this.#activeStartKeys.has(startKey)) {
      debugLog('GettingStarted', 'start ignored while action is active', { widgetId: module.id, stepId });
      return;
    }
    this.#activeStartKeys.add(startKey);
    const release = () => this.#activeStartKeys.delete(startKey);

    let releaseAsynchronously = false;
    try {
      const target = step.target;
      switch (target?.type) {
        case 'route':
          releaseAsynchronously = this.#startRoute(module, step, release);
          break;
        case 'createForm':
          releaseAsynchronously = this.#startCreateForm(module, step, release);
          break;
        case 'interfaceAction':
          releaseAsynchronously = this.#startInterfaceAction(module, step, release);
          break;
        default:
          // 'none' (or an unknown type): the step is informational, so pressing Start can only mean
          // "I did this" — which is exactly what a manual completion records.
          this.#markManualDoneIfManual(module, step);
      }
    } finally {
      if (!releaseAsynchronously) {
        release();
      }
    }
  }

  // markManualDone() used to live here but nothing called it: a manual tick rides on start()'s
  // default branch, because a target-less manual step renders exactly one affordance and pressing
  // it IS the tick. A second entry point would have been a way to tick a step without the panel.

  /** Record a skip. Only steps the admin marked `skippable` can be skipped. */
  skip(module: Module | undefined, stepId: string): void {
    const step = this.configFor(module)?.steps?.find(candidate => candidate.id === stepId);
    if (!module || !step?.skippable) {
      debugLog('GettingStarted', 'skip refused', { widgetId: module?.id, stepId, skippable: step?.skippable });
      return;
    }

    const state = this.#stateFor(module.id);
    if (state.skippedStepIds.includes(stepId)) {
      return;
    }
    this.#writeState(module.id, { skippedStepIds: [...state.skippedStepIds, stepId] });
  }

  /** Expand/collapse. Debounced: the header is a click target users play with. */
  toggle(module: Module | undefined): void {
    if (!module) {
      return;
    }
    this.#writeState(module.id, { collapsed: !this.#stateFor(module.id).collapsed }, true);
  }

  /** Close the panel for good. Only allowed when the admin made it dismissible. */
  dismiss(module: Module | undefined): void {
    const config = this.configFor(module);
    if (!module || !config?.dismissible) {
      debugLog('GettingStarted', 'dismiss refused', { widgetId: module?.id, dismissible: config?.dismissible });
      return;
    }
    this.#writeState(module.id, { dismissed: true });
  }

  /**
   * Re-evaluate every `entriesExist` step of every applicable widget.
   *
   * Safe to call as often as you like: one pass runs at a time and a call that arrives mid-pass is
   * collapsed into a single follow-up pass, so a burst of triggers (app start + dashboard
   * navigation + panel open) can never fan out into overlapping request storms.
   */
  refresh(options?: { force?: boolean }): void {
    const queries = collectExternalEntriesQueries(this.#allStepFilters());
    if (!queries.length) {
      return;
    }

    // The config-driven effect can fire on any store emission, so an unchanged set of questions is
    // skipped — otherwise a modules refresh would re-issue every count. An explicit refresh (panel
    // opened, action ran, navigated back to the dashboard) always goes through: there the whole
    // point is that the ANSWERS may have changed while the questions did not.
    const signature = queries
      .map(query => `${query.key}#${query.top}`)
      .sort()
      .join('|');
    if (!options?.force && signature === this.#lastQuerySignature) {
      return;
    }

    // Pass-collapsing, deduplication and error swallowing all live in the evaluator now.
    this.#externalEntries.resolve(queries).subscribe(resolution => {
      // Record the signature only once every question in the set actually has an answer.
      // Recording it up front would mean one transient 500 (the dev-api does those) permanently
      // silences the non-forced path: the guard would report "already asked" while nothing was
      // ever stored. A queued pass lands here early with an incomplete map for the same reason,
      // and is likewise not recorded, so the follow-up pass still runs.
      const answeredKeys = new Set(resolution.answeredKeys);
      const allAnswered = queries.every(query => answeredKeys.has(query.key));
      this.#lastQuerySignature = allAnswered ? signature : null;
    });
  }

  /**
   * Every data-driven completion condition across all applicable widgets, as filter items.
   *
   * `entriesExist` is the same question as a `basedOn: 'externalEntries'` condition, so it is
   * translated into one rather than counted separately — one evaluator, one request-deduplication
   * pass, whichever way the step was authored.
   *
   * Dismissed widgets are skipped. A widget hidden by `hideWhenComplete` is still evaluated on the
   * normal throttled triggers: its completion is live data, so deleting the only matching entry
   * must make the panel reappear without a hard reload.
   */
  #allStepFilters(): FilterItem[] {
    const filters: FilterItem[] = [];

    for (const module of this.#candidateWidgets()) {
      const config = this.configFor(module);
      if (!config?.steps?.length || (config.dismissible && this.#stateFor(module.id).dismissed)) {
        continue;
      }
      filters.push(...this.#stepFiltersFor(module));
    }

    return filters;
  }

  /**
   * The data-driven completion questions of ONE widget.
   *
   * Split out of `#allStepFilters` because `#stateSettled` needs exactly this subset: whether the
   * panel may paint depends on the questions that panel asks, not on another widget's.
   */
  #stepFiltersFor(module: Module): FilterItem[] {
    const filters: FilterItem[] = [];

    for (const step of this.configFor(module)?.steps ?? []) {
      const completion = step.completion;
      if (completion?.type === 'entriesExist') {
        if (!completion.moduleCode) {
          debugLog('GettingStarted', 'entriesExist step without moduleCode', {
            widgetId: module.id,
            stepId: step.id,
          });
          continue;
        }
        filters.push(externalEntriesFilterItem(completion.moduleCode, completion.filter, completion.minCount));
        continue;
      }
      if (completion?.type === 'condition' && completion.conditions?.length) {
        filters.push(...conditionsToFilterItems(completion.conditions));
      }
    }

    return filters;
  }

  #startRoute(module: Module, step: GettingStartedStep, release: () => void): boolean {
    const route = step.target?.route?.trim();
    // Same-origin paths only: a stored `//host` or `javascript:` value would navigate off the app.
    if (!route || !route.startsWith('/') || route.startsWith('//')) {
      debugLog('GettingStarted', 'route target rejected', { stepId: step.id, route });
      return false;
    }
    void this.#router.navigateByUrl(route).finally(release);
    this.#markManualDoneIfManual(module, step);
    return true;
  }

  #startCreateForm(module: Module, step: GettingStartedStep, release: () => void): boolean {
    const target = step.target;
    const targetModule = target?.moduleCode ? this.#modulesStore.getModuleByCode(target.moduleCode) : undefined;
    if (!targetModule) {
      debugLog('GettingStarted', 'createForm target module not found', {
        stepId: step.id,
        moduleCode: target?.moduleCode,
      });
      return false;
    }

    // Resolves the form via the step's formId, falling back to targetModule.createFormId.
    const result = this.#modalService.resolveCreateRunner(targetModule, undefined, target?.formId);
    switch (result.status) {
      case 'opened':
        result.ref
          .afterClosed()
          .pipe(finalize(release))
          .subscribe(() => {
            this.#markManualDoneIfManual(module, step);
            // The wizard may have created the very entry a step counts on, so re-evaluate rather
            // than wait for the next dashboard visit. force: the questions are unchanged, the
            // answers are exactly what may have moved.
            this.refresh({ force: true });
          });
        return true;
      case 'fallback':
        // No usable runner configured — the module's own create screen is the honest equivalent.
        if (targetModule.slug) {
          void this.#router.navigate(['/', targetModule.slug, 'nieuw']).finally(release);
          this.#markManualDoneIfManual(module, step);
          return true;
        } else {
          debugLog('GettingStarted', 'createForm fallback without slug', { stepId: step.id });
        }
        return false;
      case 'pending':
        // ModulesStore is mid-reload; ModalService retries and opens the runner itself. Doing
        // anything here (or falling back) would open a second, wrong screen.
        debugLog('GettingStarted', 'createForm runner pending', { stepId: step.id });
        setTimeout(release, REFRESH_AFTER_ACTION_MS);
        return true;
      default:
        // 'denied' — the user has already been notified by ModalService.
        debugLog('GettingStarted', 'createForm denied', { stepId: step.id });
        return false;
    }
  }

  #startInterfaceAction(module: Module, step: GettingStartedStep, release: () => void): boolean {
    const target = step.target;
    const entity = target?.moduleCode ? this.#modulesStore.getModuleByCode(target.moduleCode) : undefined;
    const interfaceAction: InterfaceAction | undefined = entity?.interfaceActions?.find(
      action => action.id === target?.actionId
    );
    if (!entity || !interfaceAction) {
      debugLog('GettingStarted', 'interfaceAction not found', {
        stepId: step.id,
        moduleCode: target?.moduleCode,
        actionId: target?.actionId,
      });
      return false;
    }

    // The action's own role gate, on top of the widget's allowedUserRoles.
    if (!this.#interfaceActionsService.isAllowedForCurrentUser(interfaceAction)) {
      debugLog('GettingStarted', 'interfaceAction not allowed for current user', {
        stepId: step.id,
        actionId: interfaceAction.id,
      });
      return false;
    }

    const entry = this.#entryForContext(target?.entryContext);
    if (!entry?.id) {
      debugLog('GettingStarted', 'interfaceAction without row context', {
        stepId: step.id,
        entryContext: target?.entryContext ?? 'currentUser',
      });
      return false;
    }

    // navigateBack: false — the panel is part of the page the user is on; the legacy row-action
    // "go one step back" would navigate away from the dashboard.
    this.#interfaceActionsService.handleCustomAction({ id: entry.id, entry, interfaceAction }, entity, true, false);
    this.#markManualDoneIfManual(module, step);
    setTimeout(() => {
      this.refresh({ force: true });
      release();
    }, REFRESH_AFTER_ACTION_MS);
    return true;
  }

  /** Row context for an interface action. Defaults to the logged-in user's own entry. */
  #entryForContext(entryContext: GettingStartedStep['target']['entryContext']): Entry | undefined {
    switch (entryContext) {
      case 'currentCompany':
        return this.#userStore.currentCompanyEntry();
      case 'currentLocation':
        return this.#userStore.currentLocationEntry();
      default:
        return this.#userStore.currentUserEntry();
    }
  }

  #markManualDoneIfManual(module: Module, step: GettingStartedStep): void {
    if (step.completion?.type !== 'manual') {
      return;
    }
    const state = this.#stateFor(module.id);
    if (state.manualDoneStepIds.includes(step.id)) {
      return;
    }
    this.#writeState(module.id, { manualDoneStepIds: [...state.manualDoneStepIds, step.id] });
  }

  #isStepCompleted(
    step: GettingStartedStep,
    state: GettingStartedUserState,
    counts: Record<string, ExternalEntriesCount>,
    userEntry: Entry | undefined
  ): boolean {
    const completion = step.completion;
    switch (completion?.type) {
      case 'manual':
        return state.manualDoneStepIds.includes(step.id);

      case 'entriesExist':
        // Kept as an authoring shortcut (module + filter + count, no condition builder), but it is
        // the same question as a 'externalEntries' condition and goes through the same evaluator.
        return this.#externalEntries.isSatisfied(completion.moduleCode, completion.filter, completion.minCount);

      case 'condition': {
        if (!completion.conditions?.length) {
          return false;
        }
        const filters = conditionsToFilterItems(completion.conditions);
        if (!filters.length) {
          return false;
        }
        // A set of purely external-entries conditions asks nothing of the user entry, so it stays
        // answerable even before /user/me has resolved.
        const needsUserEntry = filters.some(filter => filter.basedOn !== 'externalEntries');
        if (needsUserEntry && !userEntry) {
          return false;
        }
        try {
          // Candidate and context are both the current user entry: interface conditions are
          // authored against "the entry you are looking at", which here is the user themselves.
          const entry = userEntry ?? ({} as Entry);
          return evaluateFiltersAgainstEntry(entry, entry, filters, undefined, new Date(), counts);
        } catch (error) {
          debugLog('GettingStarted', 'condition evaluation failed', { stepId: step.id, error });
          return false;
        }
      }

      default:
        return false;
    }
  }

  #stateFor(widgetModuleId: string): GettingStartedUserState {
    const stored = this.#bucket()[widgetModuleId];
    return {
      skippedStepIds: Array.isArray(stored?.skippedStepIds) ? stored.skippedStepIds : [],
      manualDoneStepIds: Array.isArray(stored?.manualDoneStepIds) ? stored.manualDoneStepIds : [],
      collapsed: stored?.collapsed ?? true,
      dismissed: stored?.dismissed ?? false,
    };
  }

  /**
   * Persist a patch of one widget's per-user state.
   *
   * `editUI` merges only at the top level of `ui`, so the whole `gettingStarted` bucket is rebuilt
   * here — otherwise saving one widget would drop the others.
   */
  #writeState(widgetModuleId: string, patch: Partial<GettingStartedUserState>, debounced = false): void {
    const next: GettingStartedUiBucket = {
      ...this.#bucket(),
      [widgetModuleId]: { ...this.#stateFor(widgetModuleId), ...patch },
    };

    if (debounced) {
      this.#userPreferenceStore.editUIDebounced({ [GETTING_STARTED_UI_KEY]: next });
      return;
    }
    this.#userPreferenceStore.editUI({ [GETTING_STARTED_UI_KEY]: next });
  }
}

/**
 * The helpers below are module-scoped functions rather than static members: TypeScript forbids
 * `static #private` members on a decorated class (TS18036), and they need no instance state.
 */

/**
 * `Condition` (the API POCO) → `FilterItem` (what the FE evaluator speaks). The interface-context
 * conditions field authors no `basedOn`, so `static` is the correct default: compare the entry's
 * field against the configured value.
 *
 * `compareSubField` is intentionally ignored — it points at a field on a *linked* entry, which the
 * FE evaluator cannot resolve (the same limitation prominence conditions have).
 *
 * `externalEntries` is the exception to the compareField/operator requirement: it asks about other
 * entries entirely, so it carries neither.
 */
function conditionsToFilterItems(conditions: Condition[]): FilterItem[] {
  return conditions
    .filter(
      (condition): condition is AuthoredCondition =>
        (!!condition?.compareField && !!condition?.operator) ||
        (condition as AuthoredCondition)?.basedOn === 'externalEntries'
    )
    .map(condition =>
      condition.basedOn === 'externalEntries'
        ? externalEntriesFilterItem(condition.moduleCode, condition.filter, condition.minCount)
        : {
            compareField: condition.compareField as string,
            operator: condition.operator as string,
            basedOn:
              condition.basedOn === 'dynamic' || condition.basedOn === 'currentDate'
                ? condition.basedOn
                : ('static' as const),
            fieldKeyPath: condition.fieldKeyPath ?? '',
            subFieldKeyPath: condition.subFieldKeyPath,
            value: condition.value,
            extract: condition.extract,
            useOffset: condition.useOffset,
            dateOffset: condition.dateOffset,
            basedOn2: condition.basedOn2,
            value2: condition.value2,
            useOffset2: condition.useOffset2,
            dateOffset2: condition.dateOffset2,
          }
    );
}

/**
 * The `FilterItem` shape of an external-entries question.
 *
 * `compareField` / `operator` / `fieldKeyPath` / `value` are inert for this `basedOn` (the
 * evaluator short-circuits before touching them) but `FilterItem` requires them, so they are filled
 * with empties rather than cast away.
 */
function externalEntriesFilterItem(moduleCode?: string, filter?: string, minCount?: number): FilterItem {
  return {
    compareField: '',
    operator: '',
    basedOn: 'externalEntries' as const,
    fieldKeyPath: '',
    value: null,
    moduleCode,
    filter,
    minCount,
  };
}

// placementsOf / isRoleAllowed / toStringArray now live in shared/services/global-widget.util.ts —
// the counts widget (#20713) applies the identical gates, and two copies of "who may see this
// widget, and where does it render" is a correctness risk rather than mere duplication.
